import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DomainValue } from '@/common/dropdowns';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import type { DROPDOWN_NAMES } from '@/hooks/use-dropdowns';
import { getDropdownsOptions } from '@/queries/appconfig/get-dropdowns';

import { FormControl } from '../ui/form';

export type ConfigSelectProps = {
	dropdown: DROPDOWN_NAMES;

	value?: string | null;
	onChange?: (value: string) => void;

	placeholder?: string;
	disabled?: boolean;

	defaultValue?: string;
	firstOptionAsDefault?: boolean;
};

type Option = {
	value: number | string;
	label: string;
};
type GroupedOption = {
	group: string;
	options: Option[];
};

type ConfigGroupedOption = {
	options: GroupedOption[];
	isGroup: true;
};

type ConfigUngroupedOption = {
	options: Option[];
	isGroup: false;
};

export function ConfigSelect({
	dropdown,
	value,
	onChange,
	placeholder,
	disabled,
	defaultValue,
	firstOptionAsDefault,
}: ConfigSelectProps) {
	const { t } = useTranslation();
	const effectivePlaceholder = placeholder ?? t('damages.select.defaultPlaceholder');
	const { data: domainValuesRes, isLoading } = useQuery(getDropdownsOptions());
	const domainValues = domainValuesRes?.domainValues || [];

	const { options, isGroup } = useMemo((): ConfigUngroupedOption | ConfigGroupedOption => {
		const rootDomain = domainValues.find((d) => d.value === 'ROOT');
		if (!rootDomain) {
			return {
				options: [],
				isGroup: false,
			};
		}
		const filtered = domainValues.filter((d) => d.group === dropdown);

		const parents = filtered.filter((d) => rootDomain.id === d.parentId);
		const children = filtered.filter((d) => rootDomain.id !== d.parentId);

		const map: Record<number, DomainValue[]> = {};

		for (const child of children) {
			if (!child.parentId) {
				continue;
			}
			if (!map[child.parentId]) {
				map[child.parentId] = [];
			}
			map[child.parentId].push(child);
		}

		const parentMap = parents.map((parent) => ({
			group: parent.label,
			options: (map[parent.id] || []).map((c) => ({
				value: c.id,
				label: c.label,
			})),
		}));

		// If all parents have no children, we can treat it as ungrouped
		if (parentMap.every((g) => g.options.length === 0)) {
			return {
				options: filtered.map((d) => ({
					value: d.id,
					label: d.label,
				})),
				isGroup: false,
			};
		}
		// Otherwise, we treat it as grouped, even if some parents have no children
		return {
			options: parentMap,
			isGroup: true,
		};
	}, [domainValues, dropdown]);

	// Determine default value based on props and options
	const defaultValueToUse = useMemo(() => {
		if (firstOptionAsDefault) {
			if (isGroup) {
				for (const group of options as GroupedOption[]) {
					if (group.options.length > 0) {
						return group.options[0].value;
					}
				}
			} else {
				const opts = options as Option[];
				if (opts.length > 0) {
					return opts[0].value;
				}
			}
		}
		return defaultValue;
	}, [firstOptionAsDefault, isGroup, options, defaultValue]);

	// Sync default value to parent if needed, but only if parent doesn't already have a value. Also only do this on mount or when defaultValue changes, to avoid overwriting user selections.
	useEffect(() => {
		if (!defaultValueToUse) return;

		// if parent already has same value → do nothing
		if (value === defaultValueToUse) return;

		// if parent already has some value → do nothing
		if (value !== null && value !== undefined) return;

		onChange?.(String(defaultValueToUse));
	}, [value, defaultValueToUse, onChange]);

	// If value is null or undefined, use the defaultValueToUse. This allows the parent to control the value, but if it doesn't provide one, we fall back to our internally determined default.
	const selectedValue = value ?? defaultValueToUse;

	return (
		<Select value={selectedValue ? String(selectedValue) : ''} onValueChange={onChange} disabled={disabled}>
			<FormControl>
				<SelectTrigger className="bg-input h-8 text-sm w-full sm:w-auto">
					<SelectValue placeholder={effectivePlaceholder} />
				</SelectTrigger>
			</FormControl>

			<SelectContent className="max-h-75">
				{isLoading && (
					<div className="p-2 text-sm text-muted-foreground text-center">{t('damages.select.loading')}</div>
				)}
				{options.length === 0 && !isLoading && (
					<div className="p-2 text-sm text-muted-foreground text-center">{t('damages.select.noOptions')}</div>
				)}
				{isGroup &&
					options.map((group) => (
						<SelectGroup key={group.group}>
							<SelectLabel className="text-muted-foreground text-xs uppercase tracking-wider pl-2 py-1 bg-muted font-bold">
								{group.group}
							</SelectLabel>

							{group.options.map((opt) => (
								<SelectItem key={String(opt.value)} value={String(opt.value)}>
									{opt.label}
								</SelectItem>
							))}
						</SelectGroup>
					))}
				{!isGroup &&
					options.map((opt) => (
						<SelectItem key={String(opt.value)} value={String(opt.value)}>
							{opt.label}
						</SelectItem>
					))}
			</SelectContent>
		</Select>
	);
}
